<template>
    <auth-box class="max-w-[480px]" :errors="props.errors">
        <form
            id="login-form"
            ref="formRef"
            method="POST"
            action="/login"
            class="flex flex-col gap-4"
            novalidate
            @submit.prevent="submit()"
        >
            <input name="remember" value="1" type="hidden" />
            <div>
                <vx-label> Email </vx-label>
                <vx-input
                    v-model="data.email"
                    type="email"
                    autofocus
                    autocomplete="username"
                    name="email"
                    id="email"
                    size="lg"
                    data-test="email"
                    :error="v$.email.$error ? 'Email is invalid' : discoveryError"
                >
                </vx-input>
            </div>

            <div>
                <vx-label> Password </vx-label>
                <vx-input
                    id="password"
                    type="password"
                    v-model="data.password"
                    autocomplete="current-password"
                    data-test="password"
                    togglePassword
                    name="password"
                    size="lg"
                    :error="v$.password.$error ? 'Password is invalid' : ''"
                ></vx-input>
                <a
                    class="flex items-center mt-2 underline text-slate-600 hover:text-slate-600 text-xs"
                    :href="`/password/reset?${stringifyQueryString({ email: data.email })}`"
                >
                    <font-awesome-icon widthAuto :icon="faCircleInfo" class="mr-1"></font-awesome-icon>
                    Forgot Password?
                </a>
            </div>

            <div ref="recaptchaRef"></div>

            <div v-if="step === 'picker'" class="flex flex-col gap-2 mt-4" data-test="team-picker">
                <div class="text-sm text-slate-600">Choose how to log in.</div>
                <vx-button
                    v-for="team in discoveredTeams"
                    :key="team.id"
                    :href="team.login_url"
                    color="primary"
                    size="lg"
                    data-test="team-login"
                >
                    Log In with SSO: {{ team.name }}
                    <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
                <a href="#" class="block mx-auto text-slate-600 hover:text-slate-600 text-xs" data-test="use-password" @click.prevent="choosePassword()">
                    Use a password instead
                </a>
            </div>

            <div v-else class="flex flex-col gap-2 mt-4 md:flex-row-reverse">
                <vx-button
                    @click.prevent="submit()"
                    :loading="submitting && !submittingSso"
                    data-test="submit"
                    class="md:grow"
                    type="submit"
                    color="primary"
                    size="lg"
                >
                    Log In
                    <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
                <vx-button color="muted" size="lg" data-test="use-sso" :loading="submitting && submittingSso" @click.prevent="submit(true)">
                    Log In with SSO
                </vx-button>
            </div>

            <a
                class="block mx-auto text-slate-600 hover:text-slate-600 text-xs"
                data-test="first-time-login"
                :href="`/password/reset?${stringifyQueryString({ email: data.email, first_time: 1 })}`"
            >
                First Time Login →
            </a>
        </form>
    </auth-box>
</template>
<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AuthBox from '../AuthBox.vue';
import { VxLabel, VxInput, VxButton } from '@voxie/frontend-components';
import { faArrowRight, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { ref, reactive, onMounted, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { focusInvalidField, validateRules } from '~/utils/validation';
import { stringifyQueryString } from '~/utils/string';
import { useRecaptcha } from '~/composables/useRecaptcha';
import { ssoClient } from '~/services';
import { HTTP_TOO_MANY_REQUESTS } from '~/support/HttpStatuses';

const props = defineProps({
    recaptchaKey: String,
    errors: Array,
    oldEmail: String,
});

const recaptcha = useRecaptcha(props.recaptchaKey ?? '');
const formRef = ref(null);
const recaptchaRef = ref(null);

onMounted(() => {
    if (recaptchaRef.value) {
        recaptcha.render(recaptchaRef.value);
    }
});

const data = reactive({
    email: props.oldEmail || '',
    password: '',
});

const step = ref('form');
const passwordChosen = ref(Boolean(props.oldEmail));
const submitting = ref(false);
const submittingSso = ref(false);
const discoveryError = ref('');
const discoveredTeams = ref([]);

const v$ = useVuelidate(
    {
        email: { required, email },
        password: { required },
    },
    data
);

const choosePassword = async () => {
    step.value = 'form';
    passwordChosen.value = true;
    await submit();
};

watch(
    () => data.email,
    () => {
        passwordChosen.value = false;

        if (step.value === 'picker') {
            step.value = 'form';
            discoveredTeams.value = [];
        }
    }
);

const logInWithPassword = async () => {
    const isFormCorrect = await validateRules(v$);
    if (!isFormCorrect) {
        submitting.value = false;
        return;
    }

    // A token is good for one request, and the lookup may have spent the widget's.
    recaptcha.reset();

    try {
        await recaptcha.execute();
    } catch {
        submitting.value = false;
        return;
    }

    formRef.value?.submit();
};

const discover = async (recaptchaToken, sso) => {
    const email = data.email;
    // The person may retype the email while the answer is out; a stale answer must not act.
    const stale = () => data.email !== email;

    try {
        const { data: response } = await ssoClient.discover(email, recaptchaToken);

        if (stale()) {
            return false;
        }

        // Chrome hands script an autofilled password only after a gesture, so it is read once the answer is back.
        if (response.teams.length === 1 && (sso || data.password === '')) {
            window.location.assign(response.teams[0].login_url);
            return false;
        }

        if (response.teams.length > 0) {
            discoveredTeams.value = response.teams;
            step.value = 'picker';
            return false;
        }

        if (sso) {
            discoveryError.value = "Single sign-on isn't set up for this email address.";
            return false;
        }
    } catch (error) {
        if (stale()) {
            return false;
        }

        if (error?.response?.status === HTTP_TOO_MANY_REQUESTS && (sso || data.password === '')) {
            discoveryError.value = 'Too many attempts. Wait a minute and try again.';
            return false;
        }

        if (sso) {
            discoveryError.value = 'Something went wrong. Try again.';
            return false;
        }
    }

    return true;
};

const submit = async (sso = false) => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    submittingSso.value = sso;
    discoveryError.value = '';

    const isEmailCorrect = await v$.value.email.$validate();
    if (!isEmailCorrect) {
        focusInvalidField();
        submitting.value = false;
        return;
    }

    if (passwordChosen.value && !sso) {
        await logInWithPassword();
        return;
    }

    recaptcha.reset();

    let recaptchaToken;
    try {
        recaptchaToken = await recaptcha.execute();
    } catch {
        submitting.value = false;
        return;
    }

    if (await discover(recaptchaToken, sso)) {
        await logInWithPassword();
        return;
    }

    submitting.value = false;
};

Object.keys(window.sessionStorage)
    .filter((key) => /displayedNotActiveTeam/.test(key))
    .forEach((key) => window.sessionStorage.removeItem(key));
</script>
